<template>
  <app-collapse class="mt-1">
    <app-collapse-item
      :is-visible="false"
      class="collapsible-section-bg"
      title="General information about payment"
    >
      <template #header>
        <span
          class="lead collapse-title"
        >
          <section-status-icon
            :has-data="hasPaymentData"
            :has-errors="hasSectionErrors"
          />
          General information about payment
        </span>
      </template>
      <validation-observer>
        <!-- Payment Information Section -->
        <b-row v-if="parcel.payment_data">
          <b-col
            cols="12"
            md="3"
          >
            <validation-provider
              v-slot="{ errors }"
              name="Person paying the customs duty"
              rules="required"
            >
              <b-form-group>
                <label>
                  Person paying the customs duty <span class="text-danger">*</span>
                  <small class="text-muted">1321017000</small>
                  <feather-icon
                    v-b-tooltip.hover.top="'Person paying the customs duty'"
                    icon="InfoIcon"
                    size="14"
                    class="ml-50 text-muted"
                  />
                </label>
                <b-form-input
                  v-model="parcel.payment_data.person_paying_customs_duty"
                  v-b-tooltip.hover.top="getIssueMessage('payment_data.person_paying_customs_duty')"
                  :class="getIssueClass('payment_data.person_paying_customs_duty')"
                  :disabled="disabled"
                  :state="errors.length > 0 ? false : null"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </b-form-group>
            </validation-provider>

            <validation-provider
              v-slot="{ errors }"
              name="Deferment of payment authorisation (CGU and/or DPO) reference number"
            >
              <b-form-group>
                <label>
                  Deferment of payment authorisation (CGU and/or DPO) reference number <span class="text-danger">*</span>
                  <small class="text-muted">1210000000</small>
                </label>
                <b-form-input
                  v-model="parcel.payment_data.deferred_payment"
                  v-b-tooltip.hover.top="getIssueMessage('payment_data.deferred_payment')"
                  :class="getIssueClass('payment_data.deferred_payment')"
                  :disabled="disabled"
                  :state="errors.length > 0 ? false : null"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </b-form-group>
            </validation-provider>
          </b-col>

          <b-col
            cols="12"
            md="3"
          >
            <validation-provider
              v-slot="{ errors }"
              name="Use individual not comprehensive guarantee"
              rules="required"
            >
              <b-form-group>
                <label>
                  Use individual not comprehensive guarantee <span class="text-danger">*</span>
                  <small class="text-muted">1320020000</small>
                  <feather-icon
                    v-b-tooltip.hover.top="'Use individual not comprehensive guarantee'"
                    icon="InfoIcon"
                    size="14"
                    class="ml-50 text-muted"
                  />
                </label>
                <b-form-input
                  v-model="parcel.payment_data.prefer_individual_guarantee"
                  v-b-tooltip.hover.top="getIssueMessage('payment_data.prefer_individual_guarantee')"
                  :class="getIssueClass('payment_data.prefer_individual_guarantee')"
                  :disabled="disabled"
                  :state="errors.length > 0 ? false : null"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </b-form-group>
            </validation-provider>
          </b-col>

          <b-col
            cols="12"
            md="3"
          >
            <validation-provider
              v-slot="{ errors }"
              name="Use deposit for individual guarantee"
            >
              <b-form-group>
                <label>
                  Use deposit for individual guarantee
                  <small class="text-muted">1320030000</small>
                  <feather-icon
                    v-b-tooltip.hover.top="'Use deposit for individual guarantee'"
                    icon="InfoIcon"
                    size="14"
                    class="ml-50 text-muted"
                  />
                </label>
                <b-form-input
                  v-model="parcel.payment_data.use_deposit_for_individual_guarantee"
                  v-b-tooltip.hover.top="getIssueMessage('payment_data.use_deposit_for_individual_guarantee')"
                  :class="getIssueClass('payment_data.use_deposit_for_individual_guarantee')"
                  :disabled="disabled"
                  :state="errors.length > 0 ? false : null"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </b-form-group>
            </validation-provider>
          </b-col>

          <b-col
            cols="12"
            md="3"
          >
            <validation-provider
              v-slot="{ errors }"
              name="I agree to the release of the declaration prior to the allocation of the tariff quota"
            >
              <b-form-group>
                <label>
                  I agree to the release of the declaration prior to the allocation of the tariff quota
                  <small class="text-muted">990100100A</small>
                  <feather-icon
                    v-b-tooltip.hover.top="'I agree to the release of the declaration prior to the allocation of the tariff quota'"
                    icon="InfoIcon"
                    size="14"
                    class="ml-50 text-muted"
                  />
                </label>
                <b-form-input
                  v-model="parcel.payment_data.skip_waiting_quota"
                  v-b-tooltip.hover.top="getIssueMessage('payment_data.skip_waiting_quota')"
                  :class="getIssueClass('payment_data.skip_waiting_quota')"
                  :disabled="disabled"
                  :state="errors.length > 0 ? false : null"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </b-form-group>
            </validation-provider>
          </b-col>
        </b-row>
      </validation-observer>
    </app-collapse-item>
  </app-collapse>
</template>

<script>
import {
  BRow,
  BCol,
  BFormGroup,
  BFormInput,
  VBTooltip,
} from 'bootstrap-vue'
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import AppCollapse from '@core/components/app-collapse/AppCollapse.vue'
import AppCollapseItem from '@core/components/app-collapse/AppCollapseItem.vue'
import errorHighlightingMixin from '@/mixins/errorHighlightingMixin'
import SectionStatusIcon from '@/views/shipments/declarations/modals/components/h1/SectionStatusIcon.vue'

export default {
  components: {
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    ValidationObserver,
    ValidationProvider,
    AppCollapse,
    AppCollapseItem,
    SectionStatusIcon,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  mixins: [errorHighlightingMixin],
  props: {
    parcel: {
      type: Object,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    hasPaymentData() {
      if (!this.parcel || !this.parcel.payment_data) {
        return false
      }
      const {
        person_paying_customs_duty: personPayingCustomsDuty,
        deferred_payment: deferredPayment,
        prefer_individual_guarantee: preferIndividualGuarantee,
        use_deposit_for_individual_guarantee: useDepositForIndividualGuarantee,
        skip_waiting_quota: skipWaitingQuota,
      } = this.parcel.payment_data
      return !!(
        personPayingCustomsDuty
        || deferredPayment
        || preferIndividualGuarantee
        || useDepositForIndividualGuarantee
        || skipWaitingQuota
      )
    },
    hasSectionErrors() {
      return Object.keys(this.issuesMap).some(key => key.startsWith('payment_data.'))
    },
  },
}
</script>

<style scoped lang="scss">
@import '@core/scss/base/bootstrap-extended/variables';
@import '@core/scss/base/core/mixins/declarations-mixins';

.collapsible-section-bg {
  @include collapsible-section-bg;
}

.input-warning {
  @include input-warning;
}

.input-danger {
  @include input-danger;
}
</style>
